import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Receipt, Eye, Upload, CheckCircle, AlertCircle, FileText, Calendar, ArrowLeft } from 'lucide-react';
import InvoiceViewer from '@/components/InvoiceViewer';
import CurrencySymbol from '@/components/CurrencySymbol';
import { formatCurrency, fmtNum } from '@/utils/currency';

export default function CustomerInvoices() {
  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showViewer, setShowViewer] = useState(false);

  // Helper to display price with proper currency symbol
  const PriceDisplay = ({ amount, currency = 'USD' }) => {
    const formattedAmount = fmtNum(amount || 0);
    
    if (currency === 'MVR') {
      return (
        <span>
          <CurrencySymbol currency="MVR" /> {formattedAmount}
        </span>
      );
    }
    
    const symbol = currency === 'CHN' ? '¥' : '$';
    return <span>{symbol}{formattedAmount}</span>;
  };
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadData, setUploadData] = useState({
    payment_proof_url: '',
    payment_reference_number: ''
  });
  const [uploadingProof, setUploadingProof] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'pending', 'verifying', 'verified'

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/customer/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    setViewingInvoice(invoice);
    setShowViewer(true);
  };

  const handleUploadPaymentProof = async (invoice) => {
    setSelectedInvoice(invoice);
    setShowUploadDialog(true);
    setUploadData({
      payment_proof_url: invoice.payment_proof_url || '',
      payment_reference_number: invoice.payment_reference_number || ''
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 8MB)
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File size must be less than 8MB');
      return;
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    try {
      setUploadingProof(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/upload/payment-proof`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setUploadData(prev => ({
        ...prev,
        payment_proof_url: response.data.document_url
      }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingProof(false);
    }
  };

  const handleSubmitPaymentProof = async () => {
    if (!uploadData.payment_proof_url) {
      toast.error('Please upload payment proof');
      return;
    }

    if (!uploadData.payment_reference_number || !uploadData.payment_reference_number.trim()) {
      toast.error('Please enter payment reference number');
      return;
    }

    try {
      await axios.post(
        `${API}/customer/invoices/${selectedInvoice.id}/payment-proof`,
        uploadData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Payment proof submitted successfully! Admin will review it.');
      setShowUploadDialog(false);
      setUploadData({ payment_proof_url: '', payment_reference_number: '' });
      fetchInvoices();
    } catch (error) {
      console.error('Error submitting payment proof:', error);
      toast.error('Failed to submit payment proof');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'destructive', label: 'Payment Pending', icon: AlertCircle },
      verifying: { variant: 'default', label: 'Verifying Payment', icon: FileText },
      verified: { variant: 'default', label: 'Payment Verified', icon: CheckCircle },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (filterStatus === 'all') return true;
    return invoice.payment_status === filterStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/customer-dashboard')}
              variant="outline"
              size="sm"
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <Receipt className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-800">My Invoices</h1>
              <p className="text-sm text-gray-600">View and manage your company invoices</p>
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filterStatus === 'all' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('all')}
          >
            All ({invoices.length})
          </Button>
          <Button
            variant={filterStatus === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('pending')}
          >
            Pending ({invoices.filter(i => i.payment_status === 'pending').length})
          </Button>
          <Button
            variant={filterStatus === 'verifying' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('verifying')}
          >
            Verifying ({invoices.filter(i => i.payment_status === 'verifying').length})
          </Button>
          <Button
            variant={filterStatus === 'verified' ? 'default' : 'outline'}
            onClick={() => setFilterStatus('verified')}
          >
            Verified ({invoices.filter(i => i.payment_status === 'verified').length})
          </Button>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="text-center py-8">Loading invoices...</div>
        ) : filteredInvoices.length === 0 ? (
          <Card className="p-8 text-center">
            <Receipt className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600">No invoices found</p>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredInvoices.map((invoice) => {
              const daysUntilDue = getDaysUntilDue(invoice.due_date);
              const isOverdue = daysUntilDue !== null && daysUntilDue < 0 && invoice.payment_status !== 'verified';
              const isDueSoon = daysUntilDue !== null && daysUntilDue >= 0 && daysUntilDue <= 7 && invoice.payment_status !== 'verified';

              return (
                <Card key={invoice.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-gray-800">
                          Invoice #{invoice.invoice_number}
                        </h3>
                        {getStatusBadge(invoice.payment_status)}
                        {invoice.is_credit_invoice && (
                          <Badge variant="outline" className="border-blue-500 text-blue-700">
                            Credit Invoice
                          </Badge>
                        )}
                        {isOverdue && (
                          <Badge variant="destructive" className="flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {Math.abs(daysUntilDue)} days overdue
                          </Badge>
                        )}
                        {isDueSoon && (
                          <Badge variant="outline" className="border-orange-500 text-orange-700">
                            Due in {daysUntilDue} days
                          </Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Amount</p>
                          <p className="text-2xl font-bold text-blue-600">
                            <PriceDisplay amount={invoice.total} currency={invoice.currency} />
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Due Date</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        {invoice.terms && (
                          <div>
                            <p className="text-sm text-gray-600">Payment Terms</p>
                            <p className="font-medium">{invoice.terms}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-600">Invoice Date</p>
                          <p className="font-medium">
                            {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                      </div>

                      {invoice.payment_proof_url && (
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800 font-medium mb-1">
                            {invoice.payment_status === 'verifying' ? 'Payment Proof Under Review' : 'Payment Proof Submitted'}
                          </p>
                          <p className="text-xs text-gray-600">
                            Submitted: {new Date(invoice.payment_proof_uploaded_at).toLocaleString()}
                          </p>
                        </div>
                      )}

                      {invoice.payment_rejection_reason && (
                        <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-red-800 font-medium mb-1">Payment Proof Rejected</p>
                          <p className="text-xs text-gray-600 mb-2">{invoice.payment_rejection_reason}</p>
                          <p className="text-xs text-red-700">Please re-upload a valid payment proof.</p>
                        </div>
                      )}

                      {invoice.payment_status === 'verified' && (
                        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-800 font-medium flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Payment Verified - Invoice Paid
                          </p>
                          {invoice.payment_verified_at && (
                            <p className="text-xs text-gray-600 mt-1">
                              Verified: {new Date(invoice.payment_verified_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewInvoice(invoice)}
                        className="flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Invoice
                      </Button>

                      {invoice.payment_status !== 'verified' && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleUploadPaymentProof(invoice)}
                          className="flex items-center gap-2"
                        >
                          <Upload className="w-4 h-4" />
                          {invoice.payment_proof_url ? 'Update Proof' : 'Upload Proof'}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Invoice Viewer */}
        {viewingInvoice && (
          <InvoiceViewer 
            invoice={viewingInvoice} 
            open={showViewer} 
            onClose={() => setShowViewer(false)} 
          />
        )}

        {/* Upload Payment Proof Dialog */}
        {showUploadDialog && selectedInvoice && (
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Payment Proof</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Invoice</p>
                  <p className="font-semibold">#{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Amount</p>
                  <p className="text-xl font-bold text-blue-600">
                    <PriceDisplay amount={selectedInvoice.total} currency={selectedInvoice.currency} />
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium block mb-2">
                    Payment Reference Number *
                  </label>
                  <input
                    type="text"
                    className="w-full p-2 border rounded-md"
                    value={uploadData.payment_reference_number}
                    onChange={(e) => setUploadData(prev => ({ ...prev, payment_reference_number: e.target.value }))}
                    placeholder="Enter bank reference number"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Required: Enter the reference number from your bank transaction
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium block mb-2">
                    Upload Payment Slip/Proof *
                  </label>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileUpload}
                    className="w-full p-2 border rounded-md"
                    disabled={uploadingProof}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Accepted formats: JPG, PNG, PDF (Max 8MB)
                  </p>
                </div>

                {uploadData.payment_proof_url && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Payment proof ready to submit
                    </p>
                  </div>
                )}

                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowUploadDialog(false);
                      setUploadData({ payment_proof_url: '', payment_reference_number: '' });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitPaymentProof}
                    disabled={!uploadData.payment_proof_url || !uploadData.payment_reference_number?.trim() || uploadingProof}
                  >
                    Submit Payment Proof
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
